<template>
  <ListItem
    class="flex items-center gap-[14px] group select-none hover:font-medium !py-[8px] mx-1"
    :class="{
      '!font-medium': selected,
    }"
    @click="$emit('click')"
  >
    <div class="relative">
      <div class="rounded-full overflow-hidden inline-block w-[30px] h-[30px] border">
        <img
          :src="channelImage(account, platform)"
          :alt="getAccountName()"
          class="w-full h-full object-cover"
          loading="lazy"
          @error="$event.target.src = 'https://storage.googleapis.com/lumotive-web-storage/default/profile_default.svg'"
        />
      </div>

      <img
        v-if="!isGroupedView"
        :src="getSocialIconPath()"
        :alt="platform"
        class="absolute -right-2 bottom-[-0.25rem] h-[20px]"
        :class="{ 'bg-white rounded-full': platform === 'twitter' }"
      />
    </div>

    <div class="flex-1 truncate">
      <div class="flex items-center gap-1">
        <span
          v-tooltip="{
            content: getAccountName(),
          }"
          class="text-sm font-normal text-[#3A4557] truncate"
        >
          {{ truncateString(getAccountName(), 18) }}
        </span>
        <template v-if="isPremiumTwitter()">
          <img
            v-tooltip="'X (Twitter) Premium account'"
            src="@src/assets/img/composer/premium-badge-icon.svg"
            alt="premium badge"
            class="ml-1"
          />
        </template>
      </div>

      <span class="block text-xs select-none text-gray-800">
        {{ getAccountType() }}
      </span>
    </div>

    <div class="flex items-center gap-2">
      <img
        v-if="isReconnectRequired()"
        v-tooltip="'Your Account Token is Expired'"
        src="@assets/img/composer/warning-icon.svg"
        alt=" "
        class="w-5 h-5"
      />
      <button
        v-if="!selected"
        class="text-xs font-normal border rounded-md bg-gray-400 hover:bg-gray-500 px-1 hidden group-hover:inline-flex"
        @click.stop="$emit('only')"
      >
        only
      </button>
      <Checkbox
        :model-value="selected"
      />
    </div>
  </ListItem>
</template>

<script setup>
import { ListItem, Checkbox } from '@contentstudio/ui'
import { useComposerHelper } from '@/src/modules/composer_v2/composables/useComposerHelper'
const { channelImage } = useComposerHelper()

const props = defineProps({
  account: {
    type: Object,
    required: true
  },
  platform: {
    type: String,
    required: true
  },
  selected: {
    type: Boolean,
    default: false
  },
  isGroupedView: {
    type: Boolean,
    default: false
  }
})

defineEmits(['click', 'only'])


const getAccountName = () => {
  if (props.account.platform_name) return props.account.platform_name
  if (props.account.name) return props.account.name
  if (props.account.username) return props.account.username
  return 'Unknown Account'
}

const getAccountType = () => {
  if (props.account.platform_type === 'blog') return 'Blog'
  if (props.account.type) return props.account.type
  return 'Profile'
}

const getSocialIconPath = () => {
  const iconMap = {
    facebook: 'facebook-rounded.svg',
    instagram: 'instagram-rounded.svg',
    twitter: 'twitter-rounded.svg',
    linkedin: 'linkedin-rounded.svg',
    pinterest: 'pinterest-rounded.svg',
    youtube: 'youtube-rounded.svg',
    tiktok: 'tiktok-rounded.svg',
    tumblr: 'tumblr-rounded.svg',
    gmb: 'gmb-rounded.svg',
    threads: 'threads-rounded.svg',
    bluesky: 'bluesky-rounded.svg'
  }

  // For blog accounts
  if (props.account.platform_type === 'blog') {
    const blogIconMap = {
      wordpress: 'wordpress.svg',
      medium: 'medium.svg',
      webflow: 'webflow.svg',
      tumblr: 'tumblr-rounded.svg',
      shopify: 'shopify.svg'
    }
    return require(`@assets/img/integration/${blogIconMap[props.account.integration] || 'wordpress.svg'}`)
  }

  return require(`@assets/img/integration/${iconMap[props.platform] || 'default.svg'}`)
}

const isPremiumTwitter = () => {
  return props.platform === 'twitter' &&
         ['premium', 'premiumplus', 'basic'].includes(props.account?.subscription_type?.toLowerCase())
}

const isReconnectRequired = () => {
  switch (props.platform) {
    // You can Add platforms specific logic here
    default:
      return ['invalid', 'expired'].includes(props.account?.validity)
  }
}

const truncateString = (str, length) => {
  return str.length > length ? str.substring(0, length) + '...' : str
}
</script>
